import { useEffect, useRef, useCallback } from 'react';
import { KANBAN_API_BASE_URL } from '../services/kanbanApi';
import type { KanbanWsEnvelope } from '../types/kanbanRealtime';

function httpBaseToWsBase(httpUrl: string): string {
  if (httpUrl.startsWith('https://')) return `wss://${httpUrl.slice('https://'.length)}`;
  if (httpUrl.startsWith('http://')) return `ws://${httpUrl.slice('http://'.length)}`;
  return httpUrl;
}

const WS_BASE = httpBaseToWsBase(KANBAN_API_BASE_URL.replace(/\/$/, ''));

function withJitter(ms: number): number {
  return ms + Math.floor(Math.random() * Math.min(2000, ms * 0.2));
}

function parseFrames(raw: string): KanbanWsEnvelope[] {
  const trimmed = raw.trim();
  if (!trimmed) return [];
  const lines = trimmed.includes('\n') ? trimmed.split('\n') : [trimmed];
  const out: KanbanWsEnvelope[] = [];
  for (const line of lines) {
    const s = line.trim();
    if (!s) continue;
    try {
      out.push(JSON.parse(s) as KanbanWsEnvelope);
    } catch {
      console.warn('Kanban WS: skipped non-JSON frame', s.slice(0, 120));
    }
  }
  return out;
}

export function kanbanBoardWsUrl(boardId: string): string {
  return `${WS_BASE}/kanban/ws/board/${encodeURIComponent(boardId)}`;
}

export function kanbanTeamWsUrl(team: string): string {
  return `${WS_BASE}/kanban/ws/team/${encodeURIComponent(team)}`;
}

export interface UseKanbanBoardWebSocketOptions {
  boardId: string | undefined;
  /** When false, no socket (e.g. no read permission yet). */
  enabled: boolean;
  onEnvelope: (env: KanbanWsEnvelope) => void;
  /** Called when `seq` gap detected (caller should GET /full). */
  onSeqGap?: () => void;
  /** Revalidate HTTP access after a board access change closes the socket. */
  onAccessChanged?: () => void;
}

/**
 * Board room WebSocket: exponential backoff with jitter, optional subscribe + ping.
 */
export function useKanbanBoardWebSocket(options: UseKanbanBoardWebSocketOptions): void {
  const { boardId, enabled, onEnvelope, onSeqGap, onAccessChanged } = options;
  const onEnvelopeRef = useRef(onEnvelope);
  const onSeqGapRef = useRef(onSeqGap);
  const onAccessChangedRef = useRef(onAccessChanged);
  useEffect(() => {
    onEnvelopeRef.current = onEnvelope;
  }, [onEnvelope]);
  useEffect(() => {
    onSeqGapRef.current = onSeqGap;
  }, [onSeqGap]);
  useEffect(() => {
    onAccessChangedRef.current = onAccessChanged;
  }, [onAccessChanged]);

  const lastSeqRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimers = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!boardId || !enabled) {
      clearTimers();
      lastSeqRef.current = null;
      wsRef.current?.close();
      wsRef.current = null;
      return;
    }

    let cancelled = false;

    const scheduleReconnect = () => {
      if (cancelled) return;
      const attempt = reconnectAttemptRef.current++;
      const base = Math.min(60_000, 1000 * 2 ** Math.min(attempt, 16));
      const delay = withJitter(base);
      clearTimers();
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        connect();
      }, delay);
    };

    const connect = () => {
      if (cancelled) return;
      clearTimers();
      lastSeqRef.current = null;
      try {
        const url = kanbanBoardWsUrl(boardId);
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          if (cancelled) return;
          reconnectAttemptRef.current = 0;
          try {
            ws.send(JSON.stringify({ action: 'subscribe', channels: [`board:${boardId}`] }));
          } catch {
            /* optional */
          }
          pingTimerRef.current = setInterval(() => {
            try {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ action: 'ping', nonce: String(Date.now()) }));
              }
            } catch {
              /* ignore */
            }
          }, 45_000);
        };

        ws.onmessage = (ev) => {
          if (cancelled) return;
          const text = typeof ev.data === 'string' ? ev.data : '';
          const envs = parseFrames(text);
          for (const env of envs) {
            if (env.type === 'system.connected' || env.type === 'system.subscribed') {
              lastSeqRef.current = typeof env.seq === 'number' ? env.seq : lastSeqRef.current;
              onEnvelopeRef.current(env);
              continue;
            }
            if (env.type === 'system.pong' || env.type === 'system.error') {
              onEnvelopeRef.current(env);
              continue;
            }

            const seq = env.seq;
            if (typeof seq === 'number') {
              const last = lastSeqRef.current;
              if (last !== null && seq !== last + 1) {
                onSeqGapRef.current?.();
                lastSeqRef.current = null;
              }
              lastSeqRef.current = seq;
            }
            onEnvelopeRef.current(env);
          }
        };

        ws.onerror = () => {
          ws.close();
        };

        ws.onclose = (event) => {
          wsRef.current = null;
          clearTimers();
          if (!cancelled && event.code === 4403) onAccessChangedRef.current?.();
          if (!cancelled) scheduleReconnect();
        };
      } catch {
        scheduleReconnect();
      }
    };

    connect();

    return () => {
      cancelled = true;
      clearTimers();
      reconnectAttemptRef.current = 0;
      lastSeqRef.current = null;
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [boardId, enabled, clearTimers]);
}

export interface UseKanbanTeamWebSocketOptions {
  team: string | null | undefined;
  enabled: boolean;
  onEnvelope: (env: KanbanWsEnvelope) => void;
  onSeqGap?: () => void;
}

export function useKanbanTeamWebSocket(options: UseKanbanTeamWebSocketOptions): void {
  const { team, enabled, onEnvelope, onSeqGap } = options;
  const onEnvelopeRef = useRef(onEnvelope);
  const onSeqGapRef = useRef(onSeqGap);
  useEffect(() => {
    onEnvelopeRef.current = onEnvelope;
  }, [onEnvelope]);
  useEffect(() => {
    onSeqGapRef.current = onSeqGap;
  }, [onSeqGap]);

  const lastSeqRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimers = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!team || !enabled) {
      clearTimers();
      lastSeqRef.current = null;
      wsRef.current?.close();
      wsRef.current = null;
      return;
    }

    let cancelled = false;

    const scheduleReconnect = () => {
      if (cancelled) return;
      const attempt = reconnectAttemptRef.current++;
      const base = Math.min(60_000, 1000 * 2 ** Math.min(attempt, 16));
      const delay = withJitter(base);
      clearTimers();
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        connect();
      }, delay);
    };

    const connect = () => {
      if (cancelled) return;
      clearTimers();
      lastSeqRef.current = null;
      try {
        const ws = new WebSocket(kanbanTeamWsUrl(team));
        wsRef.current = ws;

        ws.onopen = () => {
          if (cancelled) return;
          reconnectAttemptRef.current = 0;
          try {
            ws.send(JSON.stringify({ action: 'subscribe', channels: [`team:${team}`] }));
          } catch {
            /* ignore */
          }
          pingTimerRef.current = setInterval(() => {
            try {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ action: 'ping', nonce: String(Date.now()) }));
              }
            } catch {
              /* ignore */
            }
          }, 45_000);
        };

        ws.onmessage = (ev) => {
          if (cancelled) return;
          const text = typeof ev.data === 'string' ? ev.data : '';
          const envs = parseFrames(text);
          for (const env of envs) {
            if (env.type === 'system.connected' || env.type === 'system.subscribed') {
              lastSeqRef.current = typeof env.seq === 'number' ? env.seq : lastSeqRef.current;
              onEnvelopeRef.current(env);
              continue;
            }
            if (env.type === 'system.pong' || env.type === 'system.error') {
              onEnvelopeRef.current(env);
              continue;
            }
            const seq = env.seq;
            if (typeof seq === 'number') {
              const last = lastSeqRef.current;
              if (last !== null && seq !== last + 1) {
                onSeqGapRef.current?.();
                lastSeqRef.current = null;
              }
              lastSeqRef.current = seq;
            }
            onEnvelopeRef.current(env);
          }
        };

        ws.onerror = () => {
          ws.close();
        };

        ws.onclose = () => {
          wsRef.current = null;
          clearTimers();
          if (!cancelled) scheduleReconnect();
        };
      } catch {
        scheduleReconnect();
      }
    };

    connect();

    return () => {
      cancelled = true;
      clearTimers();
      reconnectAttemptRef.current = 0;
      lastSeqRef.current = null;
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [team, enabled, clearTimers]);
}
